import { datadogRum } from '@datadog/browser-rum';
import type { NextWebVitalsMetric } from 'next/app';
import { readCookieCategories } from '@zapier/cookies';

import { isProduction } from '../../../identity-utils/src/environment';

const shouldTrackToDatadog = isProduction;

// Faro ignores its own collector by default (see faro.ts); Datadog needs both of
// these knobs to ignore Faro's, because excludedActivityUrls only keeps Faro's
// beacons from extending Datadog's page-activity/loading-time computation — it
// does not stop resource collection. beforeSend is what drops the resource event.
const isFaroCollectorRequest = (url: string) => {
  const faroCollectorUrl = process.env.NEXT_PUBLIC_FARO_COLLECTOR_URL;
  return faroCollectorUrl !== undefined && url.startsWith(faroCollectorUrl);
};

// Guards setDatadogTrackingConsent against running before init (non-prod / missing token).
let isInitialized = false;

// Read analytics (OneTrust C0002) consent synchronously so already-consented visitors
// are tracked from page load rather than waiting for the consent event to re-render.
export function analyticsConsentGranted(): boolean {
  try {
    return Boolean(readCookieCategories().byName.analytics);
  } catch {
    return false;
  }
}

export function initializeDatadogRum() {
  // Don't initialize during SSR
  if (typeof window === 'undefined') {
    return;
  }

  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APP_ID;
  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
  if (!applicationId || !clientToken) {
    // eslint-disable-next-line no-console
    console.info(
      'Datadog RUM not initialized: missing applicationId or clientToken.'
    );
    return;
  }

  if (!shouldTrackToDatadog) {
    // eslint-disable-next-line no-console
    console.info(
      'Datadog RUM not initialized: not in a production environment.'
    );
    return;
  }

  if (isInitialized) {
    return;
  }

  // See https://docs.datadoghq.com/real_user_monitoring/browser/setup/client/ for
  // these parameters. The SDK auto-collects Core Web Vitals (LCP, CLS, INP, FCP) as
  // view attributes, so they no longer need manual forwarding.
  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.com',
    service: 'identity',
    version: process.env.NEXT_PUBLIC_COMMIT_SHA,
    sessionSampleRate: 10,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    // Start held unless the visitor has already consented; the consent effect in
    // _app grants once OneTrust analytics consent resolves. See setDatadogTrackingConsent.
    trackingConsent: analyticsConsentGranted() ? 'granted' : 'not-granted',
    // Inject Datadog tracing headers on `*.zapier.com` / `*.zapier-staging.com` API
    // calls. Pinned to the datadog propagator to keep the pre-v6 header behavior (the
    // v6+ default also sends `traceparent`, which the receiving servers' CORS config
    // would need to allow). See:
    // https://docs.datadoghq.com/real_user_monitoring/platform/connect_rum_and_traces/
    allowedTracingUrls: [
      {
        match: /https:\/\/(.*\.)?zapier(-staging)?\.com/,
        propagatorTypes: ['datadog'],
      },
    ],
    excludedActivityUrls: [isFaroCollectorRequest],
    beforeSend: event =>
      !(
        event.type === 'resource' && isFaroCollectorRequest(event.resource.url)
      ),
  });

  isInitialized = true;
}

// Keep RUM collection aligned with the visitor's analytics (C0002) consent as it
// resolves or changes. No-op until init has run.
export function setDatadogTrackingConsent(analyticsAllowed: boolean) {
  if (!isInitialized) {
    return;
  }
  datadogRum.setTrackingConsent(analyticsAllowed ? 'granted' : 'not-granted');
}

// Measure and report web vitals that Next.js tracks. Core Web Vitals go to Datadog
// natively via the RUM SDK; this path forwards the full set to Axiom.
// See: https://nextjs.org/docs/advanced-features/measuring-performance
export function nextJsReportWebVitals(metric: NextWebVitalsMetric) {
  reportWebVitalsAxiom(metric);
}

function reportWebVitalsAxiom(metric: NextWebVitalsMetric) {
  const url = process.env.NEXT_PUBLIC_AXIOM_INGEST_ENDPOINT;

  if (!url) {
    return;
  }

  const body = JSON.stringify({
    route: window.__NEXT_DATA__.page,
    ...metric,
  });

  const sendBeacon =
    navigator.sendBeacon && navigator.sendBeacon.bind(navigator);

  try {
    sendBeacon(url, body);
  } catch {
    fetch(url, { body, method: 'POST', keepalive: true });
  }
}
